/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { trackedTask } from 'reactiveweb/ember-concurrency';
import { queryManager } from 'ember-apollo-client';

import phorestP from 'my-phorest/assets/images/phorest-branch-logo.svg';
import branchesQuery from 'my-phorest/gql/queries/branches.graphql';

export default class BranchSelectorComponent extends Component {
  @service branchSettings;
  @service('browser/window') window;
  @service electronApp;
  @service embeddedRedirections;
  @service errorHandler;
  @service fullCalendar;
  @service router;
  @service session;
  @service swingBridge;
  @service twoWayChat;

  @queryManager apollo;

  phorestPSrc = phorestP;

  allBranches = trackedTask(this, this.loadBranchesTask);

  get targetRoute() {
    if (this.shouldPreserveRoute) {
      if (this.isJasperHistoryOfReportRoute) {
        return 'accounts.account.jasper-reports.history';
      }
      return this.router.currentRouteName;
    } else {
      return 'accounts.account.appointments';
    }
  }

  get isJasperHistoryOfReportRoute() {
    return this.router.currentRouteName.includes(
      'jasper-reports.history-of-report'
    );
  }

  get isReportViewer() {
    return this.router.currentRouteName.includes('report-viewer');
  }

  get shouldPreserveRoute() {
    return this.isJasperReportsRoute;
  }

  get isJasperReportsRoute() {
    return this.router.currentRouteName.includes('jasper-reports');
  }

  get activeBranches() {
    if (!this.args.branches) return [];
    return this.args.branches
      .filter((branch) => typeof branch.accountId === 'number')
      .sort((a, b) => a.shortName.localeCompare(b.shortName));
  }

  get queryParams() {
    if (this.isOnAppointmentsRouteWithDateParam) {
      return {
        start: this.router.currentRoute.queryParams.start,
        departmentId: null,
        view: null,
        resourceId: null,
      };
    } else if (this.shouldPreserveRoute) {
      return this.router.currentRoute.queryParams;
    }
    return {
      start: null,
      departmentId: null,
      view: null,
      resourceId: null,
    };
  }

  get hasOtherBranches() {
    return this.otherBranches.length > 0;
  }

  get isOnAppointmentsRouteWithDateParam() {
    return (
      this.router.currentRouteName.includes('accounts.account.appointments') &&
      this.router.currentRoute.queryParams?.start
    );
  }

  get manageBranchesLink() {
    const chainStaffPath = this.embeddedRedirections.chainStaffPath;
    const currentUser = this.session.currentUser;
    return `${chainStaffPath}/${currentUser.id}/branches`;
  }

  get otherBranches() {
    const activeBranchesIds = this.activeBranches.map((b) => b.id);
    const allBranches = this.allBranches?.value || [];
    return allBranches
      .filter((b) => !activeBranchesIds.includes(b.id))
      .sort((a, b) => a.shortName.localeCompare(b.shortName));
  }

  get shouldShowOpenAllBranchesButton() {
    return (
      this.activeBranches.length > 1 &&
      this.activeBranches.length <= 20 &&
      !this.swingBridge.isEmbeddedInSwing &&
      !this.electronApp.isRunningInElectron
    );
  }

  get brandThemeLogoSrc() {
    return this.branchSettings.brandThemeLogo;
  }

  @action
  resetCalendarView() {
    this.fullCalendar.reset();
  }

  @action
  openAllBranches() {
    let branchesUrls = this.activeBranches.map((branch) => {
      if (this.isOnAppointmentsRouteWithDateParam) {
        return this.router.urlFor(this.targetRoute, branch.accountId, {
          queryParams: this.queryParams,
        });
      } else if (this.shouldPreserveRoute) {
        let queryParams = this.queryParams;
        return this.router.urlFor(this.targetRoute, branch.accountId, {
          queryParams,
        });
      } else {
        return this.router.urlFor(this.targetRoute, branch.accountId);
      }
    });
    branchesUrls.forEach((url) => {
      if (url !== this.router.currentURL) {
        window.open(url);
      }
    });
  }

  @task
  *loadBranchesTask() {
    try {
      const result = yield this.apollo.query(
        {
          query: branchesQuery,
          variables: {
            first: 500,
          },
        },
        'branches'
      );
      const branches = result.edges.map((edge) => edge.node);
      this.session.branchesList = branches;

      return branches;
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }
}
